"use client";

import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import type { DateRange } from "react-day-picker";
import * as React from "react";

import { cn } from "../utils/mergeClassNames";
import { Button } from "./button";
import { Calendar } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

interface DatePickerWithRangeProps {
  value?: DateRange;
  onPick?: (date: DateRange) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

function DatePickerWithRange({
  value,
  onPick,
  placeholder = "Pick a date range",
  className,
  disabled,
}: DatePickerWithRangeProps) {
  const [open, setOpen] = React.useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          data-empty={!value?.from}
          className={cn(
            "w-[300px] justify-start px-2.5 text-left font-normal data-[empty=true]:text-muted-foreground",
            className,
          )}
          disabled={disabled}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value?.from ? (
            value.to ? (
              <>
                {format(value.from, "LLL dd, y")} -{" "}
                {format(value.to, "LLL dd, y")}
              </>
            ) : (
              format(value.from, "LLL dd, y")
            )
          ) : (
            <span>{placeholder}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          initialFocus
          mode="range"
          defaultMonth={value?.from}
          selected={value}
          onSelect={(date) => {
            if (date && onPick) {
              onPick(date);
            }
          }}
          numberOfMonths={2}
        />
      </PopoverContent>
    </Popover>
  );
}

export { DatePickerWithRange };
export type { DatePickerWithRangeProps };
